package com.checkin.shared.legal

/**
 * Textes juridiques SmartCheckin v2.0 — clauses orientées protection de l'éditeur (EVOSTAY).
 */
object LegalTexts {

    private val editorLine =
        "${LegalBrand.PRODUCT_NAME} (éditeur : ${LegalBrand.LEGAL_ENTITY}, " +
            "contact : ${LegalBrand.CONTACT_EMAIL}, site : ${LegalBrand.DOMAIN}) " +
            "fournit une plateforme de check-in numérique aux hébergeurs."

    fun privacy(): LegalDocument = LegalDocument(
        title = "Politique de confidentialité",
        updatedAt = "11 juin 2026",
        sections = listOf(
            LegalSection(
                title = "1. Rôles et responsabilités",
                paragraphs = listOf(
                    editorLine,
                    "Pour les données des Occupants (invités), l'Hôte est seul Responsable du traitement au sens de la " +
                        "loi 09-08. ${LegalBrand.PRODUCT_NAME} agit exclusivement en qualité de sous-traitant technique " +
                        "et ne détermine pas les finalités ni les moyens du traitement au-delà des fonctionnalités proposées.",
                    "L'Hôte est seul responsable de l'information des Occupants, de la licéité des finalités, du respect " +
                        "des obligations d'hébergement et de toute communication avec la CNDP. ${LegalBrand.PRODUCT_NAME} " +
                        "n'agit pas en qualité de conseil juridique ou de conformité réglementaire.",
                ),
            ),
            LegalSection(
                title = "2. Données collectées",
                paragraphs = listOf(
                    "Hôtes : identité (nom, email), identifiants de connexion (mot de passe chiffré, OAuth), données " +
                        "d'abonnement et de facturation, logements, réservations, journaux techniques.",
                    "Occupants : nom, pièce d'identité (CIN ou passeport), photographies et vidéos biométriques, contrat " +
                        "signé, scores et statuts de vérification, dates de séjour.",
                    "${LegalBrand.PRODUCT_NAME} ne revend pas les données et ne les utilise pas pour de la publicité " +
                        "ciblée ou du profilage commercial propre.",
                ),
            ),
            LegalSection(
                title = "3. Finalités et base légale",
                paragraphs = listOf(
                    "Finalités : vérification d'identité, signature et archivage du contrat, gestion des réservations, " +
                        "sécurité du service, support et facturation Hôte.",
                    "Pour les Occupants, le traitement biométrique et documentaire repose sur le consentement recueilli " +
                        "sur le parcours invité et, le cas échéant, sur les instructions de l'Hôte en sa qualité de " +
                        "Responsable. L'Hôte garantit disposer d'une base légale suffisante pour chaque réservation.",
                ),
            ),
            LegalSection(
                title = "4. Sous-traitants et localisation",
                paragraphs = listOf(
                    "Prestataires susceptibles d'intervenir : Railway (hébergement de l'API, des applications web, " +
                        "de la base PostgreSQL et des fichiers — États-Unis, Virginie), Google Gemini (OCR — États-Unis), " +
                        "Amazon Web Services / Rekognition, KMS et Cognito (vérification faciale et chiffrement — " +
                        "Irlande, Union européenne), Resend (emails — États-Unis), Lemon Squeezy (paiement Hôte — " +
                        "États-Unis), Google (OAuth — États-Unis). Les données applicatives sont principalement hébergées " +
                        "aux États-Unis.",
                    "L'Hôte accepte ces sous-traitants en souscrivant aux Services. Des clauses contractuelles et des " +
                        "mesures de sécurité encadrent les transferts, sans que ${LegalBrand.PRODUCT_NAME} ne garantisse " +
                        "une localisation exclusive des données dans un pays donné.",
                ),
            ),
            LegalSection(
                title = "5. Conservation",
                paragraphs = listOf(
                    "Par défaut : scans et selfies supprimés automatiquement " +
                        "${LegalRetentionDefaults.VERIFY_MEDIA_RETENTION_DAYS} jours après le check-out ; contrats signés " +
                        "conservés ${LegalRetentionDefaults.SIGNED_CONTRACT_RETENTION_DAYS} jours après le check-out, " +
                        "sauf purge manuelle anticipée par l'Hôte ou obligation légale contraire imposée à l'Hôte.",
                    "Compte Hôte : données conservées pendant la durée du contrat et les délais techniques de purge " +
                        "raisonnables après clôture.",
                    "${LegalBrand.PRODUCT_NAME} peut ajuster les durées pour se conformer à la loi ou à l'évolution du " +
                        "service, avec information sur la plateforme.",
                ),
            ),
            LegalSection(
                title = "6. Droits des personnes",
                paragraphs = listOf(
                    "Conformément à la loi 09-08, les personnes concernées disposent de droits d'accès, de rectification " +
                        "et de suppression, dans les limites légales.",
                    "Les demandes relatives aux Occupants doivent être adressées en priorité à l'Hôte (Responsable). " +
                        "${LegalBrand.PRODUCT_NAME} assiste l'Hôte via les outils d'export et de purge du dashboard, " +
                        "dans la limite de ses moyens techniques et sans obligation de résultat sur le délai de traitement.",
                    "Réclamation possible auprès de la CNDP (cndp.ma). Contact : ${LegalBrand.CONTACT_EMAIL}.",
                ),
            ),
            LegalSection(
                title = "7. Sécurité",
                paragraphs = listOf(
                    "Chiffrement en transit (HTTPS). Chiffrement applicatif au repos des données sensibles des Occupants " +
                        "(documents, selfies, contrats, champs identité en base) via AES-256-GCM et, en production, " +
                        "gestion des clés via AWS KMS lorsque configuré.",
                    "Aucune mesure de sécurité n'étant infaillible, ${LegalBrand.PRODUCT_NAME} ne garantit l'absence " +
                        "absolue de violation de données. Voir le DPA pour les obligations de notification.",
                ),
            ),
            LegalSection(
                title = "8. Limitation",
                paragraphs = listOf(
                    LegalClauses.AS_IS,
                    LegalClauses.NO_INDIRECT,
                    LegalClauses.LIABILITY_CAP,
                ),
            ),
        ),
    )

    fun terms(): LegalDocument = LegalDocument(
        title = "Conditions générales d'utilisation (hôtes)",
        updatedAt = "11 juin 2026",
        sections = listOf(
            LegalSection(
                title = "1. Objet et acceptation",
                paragraphs = listOf(
                    "Les présentes CGU régissent l'accès et l'utilisation de la plateforme ${LegalBrand.PRODUCT_NAME} " +
                        "par tout hébergeur professionnel ou particulier. L'inscription, la connexion ou l'utilisation " +
                        "des Services vaut acceptation sans réserve des CGU, de la politique de confidentialité et du DPA.",
                    "Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser les Services.",
                ),
            ),
            LegalSection(
                title = "2. Compte et sécurité",
                paragraphs = listOf(
                    "Vous déclarez avoir la capacité juridique de contracter et fournir des informations exactes. " +
                        "Vous êtes seul responsable de la confidentialité de vos identifiants et de toute activité " +
                        "effectuée depuis votre compte, y compris par vos préposés ou conciergeries.",
                    "${LegalBrand.PRODUCT_NAME} peut suspendre ou fermer un compte en cas de suspicion de fraude, " +
                        "de violation des CGU, de risque pour la sécurité ou sur instruction d'une autorité, " +
                        "sans préavis lorsque l'urgence l'exige.",
                ),
            ),
            LegalSection(
                title = "3. Données personnelles — rôle des parties",
                paragraphs = listOf(
                    "Pour les Données des Occupants, vous êtes Responsable du traitement ; ${LegalBrand.PRODUCT_NAME} " +
                        "est sous-traitant. Vous vous engagez à informer les Occupants, obtenir les consentements requis " +
                        "(notamment biométrie), et n'utiliser les Services que pour des finalités licites de check-in locatif.",
                    "Vous garantissez que chaque lien de vérification n'est transmis qu'aux Occupants concernés et que " +
                        "vous ne réutiliserez pas les documents à d'autres fins (marketing, revente, etc.).",
                    "Le DPA complète les présentes CGU et prévaut en cas de contradiction sur les Données des Occupants.",
                ),
            ),
            LegalSection(
                title = "4. Vérification d'identité — absence de garantie",
                paragraphs = listOf(
                    "Les outils de vérification (OCR, comparaison faciale, liveness) sont des aides techniques. Les résultats " +
                        "sont indicatifs et peuvent comporter des erreurs. Vous restez seul responsable de la décision " +
                        "d'accorder l'accès au logement et du respect des obligations légales (fiche police, registre, etc.).",
                    "${LegalBrand.PRODUCT_NAME} ne garantit ni l'identité réelle d'un Occupant, ni l'absence de fraude " +
                        "documentaire sophistiquée, ni la conformité de votre activité à la réglementation locale.",
                ),
            ),
            LegalSection(
                title = "5. Abonnement, facturation et remboursements",
                paragraphs = listOf(
                    "Les formules payantes sont facturées via Lemon Squeezy (ou tout autre prestataire indiqué sur la plateforme). " +
                        "Tarifs, limites et quotas sont ceux affichés au moment de la souscription.",
                    LegalClauses.CHANGES_ACCEPTANCE,
                    "Sauf disposition légale impérative, les sommes déjà versées ne sont pas remboursables, y compris en cas " +
                        "de non-utilisation, de résiliation par l'Hôte ou de suspension pour violation des CGU.",
                ),
            ),
            LegalSection(
                title = "6. Propriété intellectuelle",
                paragraphs = listOf(
                    "La plateforme, le code, les marques, interfaces et documentation restent la propriété exclusive de " +
                        "${LegalBrand.LEGAL_ENTITY}. Aucune cession de droits de propriété intellectuelle n'est consentie.",
                    "Licence limitée, non exclusive, non transférable et révocable d'utilisation des Services pendant la " +
                        "durée du contrat, aux seules fins prévues par les CGU.",
                ),
            ),
            LegalSection(
                title = "7. Obligations de l'Hôte",
                paragraphs = listOf(
                    "Respecter la loi 09-08, la réglementation d'hébergement applicable et les présentes conditions.",
                    "Ne pas tenter d'extraire, scraper, rétro-ingénier ou surcharger les Services.",
                    "Ne pas soumettre de contenus illicites, falsifiés ou de tiers sans autorisation.",
                    "Assumer l'ensemble des relations contractuelles et contentieuses avec les Occupants.",
                ),
            ),
            LegalSection(
                title = "8. Limitation de responsabilité",
                paragraphs = listOf(
                    LegalClauses.AS_IS,
                    LegalClauses.NO_INDIRECT,
                    LegalClauses.LIABILITY_CAP,
                    "Les limitations ci-dessus s'appliquent même si un recours n'atteint pas son objet essentiel, " +
                        "dans les limites du droit marocain impératif.",
                ),
            ),
            LegalSection(
                title = "9. Indemnisation",
                paragraphs = listOf(
                    LegalClauses.HOST_INDEMNITY,
                ),
            ),
            LegalSection(
                title = "10. Force majeure",
                paragraphs = listOf(
                    "${LegalBrand.PRODUCT_NAME} n'est pas responsable de l'inexécution ou des retards dus à un événement " +
                        "de force majeure ou hors de son contrôle raisonnable (panne réseau, cloud, prestataire tiers, " +
                        "cyberattaque massive, décision administrative, catastrophe, grève externe, etc.).",
                ),
            ),
            LegalSection(
                title = "11. Résiliation",
                paragraphs = listOf(
                    "Vous pouvez fermer votre compte à tout moment via la plateforme ou par email. ${LegalBrand.PRODUCT_NAME} " +
                        "peut résilier ou suspendre l'accès immédiatement en cas de violation, de impayé ou de risque " +
                        "pour les Occupants ou le service.",
                    "Les clauses de limitation, indemnisation, propriété intellectuelle et données survivent à la résiliation.",
                    "Contact : ${LegalBrand.CONTACT_EMAIL}.",
                ),
            ),
            LegalSection(
                title = "12. Droit applicable et litiges",
                paragraphs = listOf(
                    "Droit marocain. " + LegalClauses.JURISDICTION,
                ),
            ),
        ),
    )

    fun guestNotice(): LegalDocument = LegalDocument(
        title = "Information — traitement de vos données",
        updatedAt = "11 juin 2026",
        sections = listOf(
            LegalSection(
                title = "Finalité",
                paragraphs = listOf(
                    "Votre hébergeur utilise ${LegalBrand.PRODUCT_NAME} pour vérifier votre identité et faire signer le " +
                        "contrat de location. L'hébergeur est responsable du traitement ; ${LegalBrand.PRODUCT_NAME} " +
                        "agit en sous-traitant technique pour son compte.",
                ),
            ),
            LegalSection(
                title = "Données collectées",
                paragraphs = listOf(
                    "Pièce d'identité (CIN ou passeport), selfie / vidéo biométrique, signature du contrat et données " +
                        "de séjour associées à la réservation.",
                ),
            ),
            LegalSection(
                title = "Destinataires",
                paragraphs = listOf(
                    "Votre hébergeur, ${LegalBrand.PRODUCT_NAME}, et les prestataires techniques strictement nécessaires " +
                        "(hébergement, OCR document, vérification faciale, emails). Certains prestataires peuvent être " +
                        "situés hors du Maroc.",
                ),
            ),
            LegalSection(
                title = "Consentement et droits",
                paragraphs = listOf(
                    "Le parcours de vérification biométrique et documentaire requiert votre consentement explicite. " +
                        "Sans ce consentement, la vérification en ligne ne peut pas être poursuivie ; contactez votre " +
                        "hébergeur pour une modalité alternative sous sa responsabilité.",
                    "Pour exercer vos droits (accès, rectification, suppression), contactez d'abord votre hébergeur ou " +
                        "${LegalBrand.CONTACT_EMAIL}. Réclamation possible auprès de la CNDP.",
                ),
            ),
        ),
    )

    fun dpa(): LegalDocument = LegalDocument(
        title = "Contrat de sous-traitance — traitement des données (DPA)",
        updatedAt = "11 juin 2026",
        sections = listOf(
            LegalSection(
                title = "1. Parties",
                paragraphs = listOf(
                    "Responsable du traitement : l'Hôte utilisant ${LegalBrand.PRODUCT_NAME}.",
                    "Sous-traitant : ${LegalBrand.LEGAL_ENTITY}, éditeur de ${LegalBrand.PRODUCT_NAME}, " +
                        "${LegalBrand.CONTACT_EMAIL}, ${LegalBrand.DOMAIN}.",
                    "« Données des Occupants » : données personnelles des invités traitées via la plateforme pour le compte de l'Hôte.",
                ),
            ),
            LegalSection(
                title = "2. Objet, durée et hiérarchie",
                paragraphs = listOf(
                    "Le DPA encadre le traitement des Données des Occupants par ${LegalBrand.PRODUCT_NAME} pour le compte " +
                        "de l'Hôte (loi 09-08). Il complète les CGU et la politique de confidentialité. En cas de contradiction " +
                        "sur les Données des Occupants, le DPA prévaut.",
                    "Durée : à compter de l'acceptation (inscription ou première vérification d'Occupant) jusqu'à suppression " +
                        "des données et clôture du compte Hôte, sous réserve des délais de conservation.",
                ),
            ),
            LegalSection(
                title = "3. Instructions et finalités",
                paragraphs = listOf(
                    "${LegalBrand.PRODUCT_NAME} traite les Données des Occupants uniquement sur instruction documentée de l'Hôte " +
                        "(création de réservations, envoi de liens, paramétrage des étapes, export, purge).",
                    "Finalités limitées : vérification d'identité, contrat signé, gestion des statuts, export/suppression. " +
                        "Aucune revente ni publicité.",
                    "L'Hôte garantit que chaque instruction est licite et que les consentements requis ont été obtenus.",
                ),
            ),
            LegalSection(
                title = "4. Obligations de l'Hôte",
                paragraphs = listOf(
                    "Disposer d'une base légale valable ; informer les Occupants ; répondre en premier lieu aux demandes d'exercice des droits.",
                    "Ne pas réutiliser les documents hors check-in locatif.",
                    "Notifier sans délai ${LegalBrand.PRODUCT_NAME} de toute réclamation, réclamation CNDP ou incident " +
                        "concernant les Données des Occupants.",
                    LegalClauses.HOST_INDEMNITY,
                ),
            ),
            LegalSection(
                title = "5. Obligations de ${LegalBrand.PRODUCT_NAME}",
                paragraphs = listOf(
                    "Mettre en œuvre des mesures de sécurité raisonnables compte tenu de l'état de l'art et des risques " +
                        "(HTTPS, chiffrement applicatif au repos, contrôle d'accès, hébergement sécurisé).",
                    "Garantir la confidentialité du personnel et prestataires autorisés.",
                    "Notifier l'Hôte d'une violation affectant les Données des Occupants dès qu'elle est identifiée, " +
                        "avec les informations raisonnablement disponibles — sans que cela ne transfère à ${LegalBrand.PRODUCT_NAME} " +
                        "l'obligation de notifier la CNDP ou les Occupants.",
                    "Assister l'Hôte, sur demande écrite et dans des limites commercialement raisonnables, pour répondre " +
                        "aux demandes des personnes concernées ou de la CNDP.",
                ),
            ),
            LegalSection(
                title = "6. Sous-traitants ultérieurs",
                paragraphs = listOf(
                    "L'Hôte autorise d'avance : Railway, Google Gemini, AWS Rekognition, Resend, Lemon Squeezy (facturation Hôte uniquement), Google OAuth.",
                    "Tout changement majeur sera publié sur la politique de confidentialité. L'Hôte dispose de quinze (15) jours " +
                        "pour s'opposer par email et résilier son compte ; à défaut, le nouveau sous-traitant est réputé accepté.",
                ),
            ),
            LegalSection(
                title = "7. Transferts internationaux",
                paragraphs = listOf(
                    "L'Hôte reconnaît que des transferts hors du Maroc peuvent intervenir, notamment vers les États-Unis " +
                        "(hébergement Railway en Virginie, Google, Resend, Lemon Squeezy) et l'Union européenne — Irlande " +
                        "(Amazon Web Services pour la vérification faciale et le chiffrement), et accepte les garanties contractuelles " +
                        "mises en place par ${LegalBrand.PRODUCT_NAME}, sans garantie de localisation unique des données.",
                ),
            ),
            LegalSection(
                title = "8. Conservation et purge",
                paragraphs = listOf(
                    "Délais par défaut : " +
                        "${LegalRetentionDefaults.VERIFY_MEDIA_RETENTION_DAYS} jours (médias vérif.) / " +
                        "${LegalRetentionDefaults.SIGNED_CONTRACT_RETENTION_DAYS} jours (contrats) après check-out.",
                    "Purge manuelle et export via le dashboard. À la fin du contrat, suppression des données restantes " +
                        "sous réserve des obligations légales de l'Hôte et des sauvegardes techniques à durée limitée.",
                ),
            ),
            LegalSection(
                title = "9. Violations de données",
                paragraphs = listOf(
                    "L'Hôte, en qualité de Responsable, assume seul les notifications à la CNDP et aux Occupants lorsque la loi l'exige.",
                    "${LegalBrand.PRODUCT_NAME} coopère raisonnablement et notifie l'Hôte sans retard injustifié après découverte " +
                        "d'un incident affectant les Données des Occupants.",
                ),
            ),
            LegalSection(
                title = "10. Audits",
                paragraphs = listOf(
                    "L'Hôte peut demander des informations sur les mesures de sécurité. Tout audit sur site ou questionnaire " +
                        "étendu au-delà de la documentation standard pourra être facturé et limité à une fois par an " +
                        "moyennant préavis de trente (30) jours.",
                ),
            ),
            LegalSection(
                title = "11. Limitation de responsabilité",
                paragraphs = listOf(
                    LegalClauses.AS_IS,
                    LegalClauses.NO_INDIRECT,
                    LegalClauses.LIABILITY_CAP,
                    "${LegalBrand.PRODUCT_NAME} n'est pas responsable des manquements de l'Hôte à ses obligations de Responsable, " +
                        "ni des conséquences d'instructions illicites ou inexactes.",
                ),
            ),
            LegalSection(
                title = "12. Fin du contrat",
                paragraphs = listOf(
                    "À la clôture du compte, l'Hôte exporte les données qu'il souhaite conserver. ${LegalBrand.PRODUCT_NAME} " +
                        "supprime les Données des Occupants selon les délais et outils prévus, sauf conservation légale ou " +
                        "sauvegarde technique temporaire.",
                ),
            ),
            LegalSection(
                title = "13. Modifications",
                paragraphs = listOf(
                    LegalClauses.CHANGES_ACCEPTANCE,
                ),
            ),
            LegalSection(
                title = "14. Droit applicable",
                paragraphs = listOf(
                    "Droit marocain (loi 09-08). " + LegalClauses.JURISDICTION,
                ),
            ),
            LegalSection(
                title = "15. Acceptation",
                paragraphs = listOf(
                    "Acceptation par case à cocher à l'inscription ou, à défaut, dès la première utilisation des Services " +
                        "pour faire vérifier un Occupant. Version ${LegalVersion.CURRENT}.",
                ),
            ),
        ),
    )
}
